export const deliveryPageData = {
  stats: [
    { value: "30%", label: "Fuel Cost Reduction" },
    { value: "25%", label: "Faster Delivery Times" },
    { value: "98%+", label: "On-Time Delivery Rate" },
    { value: "0", label: "Missed Deliveries" },
  ],
  statsAccent: "from-amber-600 to-orange-600",

  challengeTitle: "Last-Mile Delivery Is Your Costliest Operational Problem",
  challengeSubtitle:
    "Inefficient routes, missed deliveries, and lack of real-time visibility are costing you customers and money every day.",
  challenges: [
    {
      icon: "🗺️",
      title: "Inefficient Delivery Routes",
      description:
        "Drivers follow manual or guesswork routes — burning fuel, wasting time, and delivering late.",
    },
    {
      icon: "📞",
      title: "Constant Customer Complaints",
      description:
        "Without accurate ETAs or proactive notifications, customers call repeatedly — overwhelming your support team.",
    },
    {
      icon: "❌",
      title: "High Failed Delivery Rate",
      description:
        "Failed deliveries due to wrong addresses, absent customers, or timing issues cost 2–5× more to redeliver.",
    },
    {
      icon: "👥",
      title: "Uncoordinated Driver Dispatch",
      description:
        "Manual driver assignment based on gut feel leads to uneven workloads, overtime costs, and delivery gaps.",
    },
    {
      icon: "💰",
      title: "Cash Reconciliation Chaos",
      description:
        "Cash on delivery collections are untracked, error-prone, and create daily reconciliation headaches.",
    },
    {
      icon: "📊",
      title: "No Delivery Performance Data",
      description:
        "Without analytics, you can't identify bottlenecks, underperforming routes, or your best drivers.",
    },
  ],
  theme: {
    challengeBg: "bg-slate-900",
    challengeTitle: "text-white",
    challengeSubtitle: "text-slate-400",
    challengeCard: "bg-slate-800/60",
    challengeBorder: "border-slate-700",
    badgeBg: "bg-amber-50 text-amber-700 border-amber-100",
    solutionIconBg: "bg-amber-50",
    solutionIconColor: "text-amber-600",
    solutionAccent: "from-amber-500 to-orange-600",
  },

  solutionTitle: "Real-Time Delivery Orchestration from Dispatch to Doorstep",
  solutionSubtitle:
    "Complete control over every delivery — intelligent dispatch, AI route optimisation, and live customer updates.",
  solutions: [
    {
      icon: "🧠",
      title: "AI Route Optimisation",
      description:
        "Generate the most fuel-efficient delivery routes in seconds using real-time traffic, order density, and driver locations.",
    },
    {
      icon: "📱",
      title: "Smart Driver Assignment",
      description:
        "Automatically assign deliveries to the nearest available driver based on location, capacity, and schedule.",
    },
    {
      icon: "🔔",
      title: "Live Customer Notifications",
      description:
        "Customers receive automatic updates at every delivery milestone with accurate AI-predicted ETAs.",
    },
    {
      icon: "💳",
      title: "Flexible Payment Options",
      description:
        "Support cash on delivery, digital wallets, card payments, and pre-paid orders — all tracked automatically.",
    },
    {
      icon: "🗺️",
      title: "Live Dispatch Dashboard",
      description:
        "See every driver's location, every order's status, and every delivery in progress on a single live map view.",
    },
    {
      icon: "📦",
      title: "Digital Proof of Delivery",
      description:
        "Drivers capture digital signatures and photos at delivery — eliminating disputes and protecting your business.",
    },
    {
      icon: "🔁",
      title: "Returns Management",
      description:
        "Handle failed deliveries and returns with automated rescheduling workflows that minimise redelivery costs.",
    },
    {
      icon: "📊",
      title: "Delivery Analytics",
      description:
        "Track on-time rates, route efficiency, driver performance, and cost per delivery with detailed reporting.",
    },
    {
      icon: "⚡",
      title: "Same-Day Delivery Capability",
      description:
        "Dynamic slot management and real-time routing enable same-day delivery offerings that win customer loyalty.",
    },
  ],

  howItWorksTitle: "Optimised Delivery Operations in 3 Simple Steps",
  howItWorksSteps: [
    {
      icon: "📋",
      title: "Orders Come In",
      description:
        "Orders from any channel flow into EkkBaz AI automatically — including your website, app, and marketplace.",
    },
    {
      icon: "🤖",
      title: "AI Plans & Assigns",
      description:
        "AI instantly generates optimised routes and assigns each order to the best available driver.",
    },
    {
      icon: "🚚",
      title: "Driver Delivers",
      description:
        "Driver follows the optimised route with turn-by-turn guidance while customers track in real time.",
    },
    {
      icon: "✅",
      title: "Instant Confirmation",
      description:
        "Digital proof of delivery is captured, payment reconciled, and customer notified — all automatically.",
    },
  ],

  outcomeTitle: "Measurable Delivery Improvements from Day One",
  outcomeSubtitle: "What businesses achieve after deploying EkkBaz AI Delivery Management.",
  outcomes: [
    {
      value: "30%",
      metric: "Fuel Cost Reduction",
      description:
        "AI-optimised routes reduce fuel consumption and vehicle wear by 30% — a significant bottom-line saving at scale.",
    },
    {
      value: "98%+",
      metric: "On-Time Delivery Rate",
      description:
        "Intelligent dispatch and real-time route adjustment achieves industry-leading on-time delivery performance.",
    },
    {
      value: "60%",
      metric: "Fewer Customer Complaints",
      description:
        "Proactive notifications with accurate ETAs eliminate most customer enquiries about delivery status.",
    },
  ],
  outcomeImage: "/assets/images/products/delivery2.png",

  ctaTitle: "Deliver Faster, Cheaper, and Better — Starting Today",
  ctaSubtitle:
    "Book a free consultation and see how EkkBaz AI can reduce your delivery costs by 30% and achieve 98%+ on-time performance.",
  ctaLabel: "Book a Free Consultation",
  ctaLink: "https://outlook.office.com/book/EkkBazInvestment@EkkBaz.com/?ismsaljsauthenabled",
};

export const deliveryData = [
  {
    id: 1,
    extraLabel: "The Challenge",
    userType: "Missed Deliveries, Angry Customers, and Rising Logistics Costs",
    description:
      "Managing last-mile delivery manually means constant firefighting — uncoordinated drivers, inaccurate ETAs, failed deliveries, and mounting customer complaints. Every missed delivery costs you a customer. Every inefficient route costs you money.",
    banner: "/assets/images/products/delivery1.png",
  },
  {
    id: 2,
    extraLabel: "Our Solution",
    userType: "Real-Time Delivery Orchestration from Dispatch to Doorstep",
    description:
      "EkkBaz AI's Delivery Management platform gives you complete control over your last-mile operations. Assign drivers intelligently based on location and capacity, offer flexible payment options (cash on delivery, pay on pickup), and monitor every order's real-time status on a live dispatch dashboard — with automatic customer notifications at every milestone.",
    banner: "/assets/images/products/delivery2.png",
  },
  {
    id: 3,
    extraLabel: "The Outcome",
    userType: "Cut Delivery Costs 30% with AI-Optimized Routes and Dispatch",
    description:
      "EkkBaz AI's route optimization engine analyzes traffic, order density, and driver availability to generate the most efficient delivery routes — reducing fuel costs by 30%, cutting average delivery time by 25%, and achieving 98%+ on-time delivery rates that keep customers coming back.",
    banner: "/assets/images/products/delivery3.png",
  },
];

export const deliveryFAQs = [
  {
    question: "How does EkkBaz AI optimize delivery routes?",
    answer:
      "EkkBaz AI's route optimization engine uses real-time traffic data, order geolocation, driver availability, and delivery time windows to automatically generate the most efficient routes — reducing fuel costs and delivery times simultaneously.",
  },
  {
    question: "What payment options does EkkBaz Delivery Management support?",
    answer:
      "EkkBaz supports multiple last-mile payment methods including cash on delivery, digital wallet payment, card on delivery, and pre-paid orders — giving your customers flexibility while ensuring accurate financial reconciliation.",
  },
  {
    question: "How does EkkBaz AI improve customer delivery experience?",
    answer:
      "EkkBaz automatically sends customers real-time delivery notifications at every status change — from dispatch to en-route to delivered — with accurate ETA predictions powered by AI, reducing failed deliveries and customer inquiries.",
  },
];
